(() => {
  if (window.variantMediaGroupsLoaded) return;
  window.variantMediaGroupsLoaded = true;

  const CONFIG_SELECTOR = 'script[data-variant-media-groups]';
  const HIDDEN_ATTRIBUTE = 'data-variant-media-hidden';
  const parsedConfigs = new WeakMap();

  const normalizeId = (value) => (value === null || value === undefined ? '' : String(value));

  const mediaIdFromAttribute = (element, attribute) => {
    const value = element.getAttribute(attribute) || '';
    const match = value.match(/(\d+)$/);
    return match ? match[1] : '';
  };

  const parseConfig = (element) => {
    if (parsedConfigs.has(element)) return parsedConfigs.get(element);

    try {
      const rawConfig = JSON.parse(element.textContent);
      const mediaIds = (rawConfig.mediaIds || []).map(normalizeId).filter(Boolean);
      const mediaIndexById = new Map(mediaIds.map((id, index) => [id, index]));
      const featuredMediaByVariant = new Map();

      (rawConfig.variants || []).forEach((variant) => {
        const variantId = normalizeId(variant.id);
        const featuredMediaId = normalizeId(variant.featuredMediaId);
        if (variantId && featuredMediaId && mediaIndexById.has(featuredMediaId)) {
          featuredMediaByVariant.set(variantId, featuredMediaId);
        }
      });

      const groupStarts = [...new Set([...featuredMediaByVariant.values()].map((id) => mediaIndexById.get(id)))].sort(
        (a, b) => a - b
      );

      const config = {
        sectionId: normalizeId(rawConfig.sectionId),
        selectedVariantId: normalizeId(rawConfig.selectedVariantId),
        mediaIds,
        mediaIndexById,
        featuredMediaByVariant,
        groupStarts,
      };

      parsedConfigs.set(element, config);
      return config;
    } catch (error) {
      console.warn('Variant media groups could not be initialized.', error);
      parsedConfigs.set(element, null);
      return null;
    }
  };

  const setMediaVisibility = (elements, allowedMediaIds, attribute) => {
    let visibleCount = 0;

    elements.forEach((element) => {
      const mediaId = mediaIdFromAttribute(element, attribute);
      const shouldHide = mediaId !== '' && !allowedMediaIds.has(mediaId);
      element.toggleAttribute(HIDDEN_ATTRIBUTE, shouldHide);
      if (!shouldHide) visibleCount += 1;
    });

    return visibleCount;
  };

  const clearMediaVisibility = (productInfo, sectionId) => {
    productInfo.querySelectorAll(`[${HIDDEN_ATTRIBUTE}]`).forEach((element) => {
      element.removeAttribute(HIDDEN_ATTRIBUTE);
    });

    const gallery = productInfo.querySelector('media-gallery');
    if (gallery) {
      const gallerySlides = gallery.querySelectorAll('[id^="Slider-Gallery-"] > li[data-media-id]');
      refreshSliders(gallery, gallerySlides.length);
    }

    document.getElementById(`ProductModal-${sectionId}`)?.querySelectorAll(`[${HIDDEN_ATTRIBUTE}]`).forEach((element) => {
      element.removeAttribute(HIDDEN_ATTRIBUTE);
    });
  };

  const refreshSliders = (gallery, visibleMediaCount) => {
    gallery.toggleAttribute('data-variant-media-single', visibleMediaCount <= 1);

    requestAnimationFrame(() => {
      gallery.querySelectorAll('slider-component').forEach((slider) => {
        slider.resetPages?.();

        const visibleSlides = Array.from(slider.querySelectorAll('[id^="Slide-"]')).filter(
          (slide) => !slide.hasAttribute(HIDDEN_ATTRIBUTE)
        );
        const currentCounter = slider.querySelector('.slider-counter--current');
        const totalCounter = slider.querySelector('.slider-counter--total');

        if (currentCounter) currentCounter.textContent = visibleSlides.length ? '1' : '0';
        if (totalCounter) totalCounter.textContent = String(visibleSlides.length);

        if (visibleSlides.length <= 1) {
          slider.querySelectorAll('button[name="previous"], button[name="next"]').forEach((button) => {
            button.setAttribute('disabled', 'disabled');
          });
        }
      });
    });
  };

  const applyMediaGroup = (configElement, variantId) => {
    const config = parseConfig(configElement);
    const productInfo = configElement.closest('product-info');
    if (!config || !productInfo) return;

    const selectedVariantId = normalizeId(variantId) || config.selectedVariantId;
    const selectedFeaturedMediaId = config.featuredMediaByVariant.get(selectedVariantId);

    // At least two distinct featured images are required to define automatic blocks.
    if (!selectedFeaturedMediaId || config.groupStarts.length < 2) {
      clearMediaVisibility(productInfo, config.sectionId);
      return;
    }

    const groupStart = config.mediaIndexById.get(selectedFeaturedMediaId);
    const nextGroupStart = config.groupStarts.find((start) => start > groupStart) ?? config.mediaIds.length;
    const firstGroupStart = config.groupStarts[0];
    const allowedMediaIds = new Set([
      ...config.mediaIds.slice(0, firstGroupStart),
      ...config.mediaIds.slice(groupStart, nextGroupStart),
    ]);

    const gallery = productInfo.querySelector('media-gallery');
    if (gallery) {
      const gallerySlides = gallery.querySelectorAll('[id^="Slider-Gallery-"] > li[data-media-id]');
      const thumbnailSlides = gallery.querySelectorAll('[id^="Slider-Thumbnails-"] > li[data-target]');
      const visibleMediaCount = setMediaVisibility(gallerySlides, allowedMediaIds, 'data-media-id');
      setMediaVisibility(thumbnailSlides, allowedMediaIds, 'data-target');
      refreshSliders(gallery, visibleMediaCount);
    }

    const modal = document.getElementById(`ProductModal-${config.sectionId}`);
    if (modal) {
      const modalMedia = modal.querySelectorAll('.product-media-modal__content > [data-media-id]');
      setMediaVisibility(modalMedia, allowedMediaIds, 'data-media-id');
    }
  };

  const applyWithin = (root, variantId) => {
    const configElements = [];

    if (root?.matches?.(CONFIG_SELECTOR)) configElements.push(root);
    root?.querySelectorAll?.(CONFIG_SELECTOR).forEach((element) => configElements.push(element));

    configElements.forEach((element) => applyMediaGroup(element, variantId));
  };

  const applyForSection = (sectionId, variantId) => {
    document.querySelectorAll(CONFIG_SELECTOR).forEach((configElement) => {
      const config = parseConfig(configElement);
      const productInfo = configElement.closest('product-info');
      const productSectionId = productInfo?.dataset.originalSection || productInfo?.dataset.section;

      if (config && (config.sectionId === normalizeId(sectionId) || productSectionId === normalizeId(sectionId))) {
        applyMediaGroup(configElement, variantId);
      }
    });
  };

  applyWithin(document);

  document.addEventListener('product-info:loaded', (event) => applyWithin(event.target));
  document.addEventListener('shopify:section:load', (event) => applyWithin(event.target));

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      applyForSection(data.sectionId, data.variant?.id);
    });
  }
})();
